import { useEffect, useMemo, useState } from 'react'

import { AxiosInstance } from 'axios'

import {
  EssentialsEventData,
  EssentialsRegisteredEventData,
  EssentialsUpcomingEventData,
  getAnalyticsPropsForEvent,
  getEssentialsEventZoomInfo,
  ZOOM_EVENT_TYPE,
  zoomEventTypeMap,
} from '@lyrahealth-inc/shared-app-logic'

export const useUpcomingEssentialsEvents = ({
  essentialsEventsFullList,
  registeredEvents,
  fetchRegisteredEvents,
  lyraId,
  axiosInstance,
  isLoggedIn = true,
}: {
  essentialsEventsFullList?: EssentialsEventData[] | null
  registeredEvents?: EssentialsRegisteredEventData[] | null
  fetchRegisteredEvents?: () => void
  lyraId?: string
  axiosInstance: AxiosInstance
  isLoggedIn?: boolean
}): {
  eventsList: EssentialsUpcomingEventData[]
  eventsMixpanelDataList: {
    mixpanelData: Record<string, any>
    otherData: Record<string, any>
  }[]
} => {
  /** Events that have availability, checked against zoom */
  const [availableEvents, setAvailableEvents] = useState<EssentialsEventData[] | null>(null)
  /** Final events to display, checked for availability and registration */
  const [upcomingEvents, setUpcomingEvents] = useState<EssentialsUpcomingEventData[]>([])
  const NUM_UPCOMING_EVENTS = 3

  useEffect(() => {
    if (fetchRegisteredEvents) {
      fetchRegisteredEvents()
    }
  }, [fetchRegisteredEvents])

  useEffect(() => {
    const fetchAvailableEvents = async () => {
      if (essentialsEventsFullList === null || !essentialsEventsFullList?.length) return

      const filterForAvailableEvents = async (eventsList: EssentialsEventData[]) => {
        if (!isLoggedIn || !lyraId) return eventsList

        const zoomEventToEssentialsEventMap: Record<string, string> = {} // Zoom id -> event id map
        const eventsMap: Record<string, EssentialsEventData> = eventsList.reduce((eventsMap, event) => {
          eventsMap[event.id] = event
          return eventsMap
        }, {} as Record<string, EssentialsEventData>) // Event id -> event map

        // Extract each zoom event from the essentials event to create a list of zoom events
        const zoomEvents = eventsList.flatMap((event) => {
          if (event.eventSeries) {
            const zoomId = event.eventSeriesTimes[0].eventId
            zoomEventToEssentialsEventMap[zoomId] = event.id
            return [{ zoom_event_id: zoomId, zoom_event_type: ZOOM_EVENT_TYPE.EVENT }]
          } else {
            return event.eventTimes.map((time) => {
              zoomEventToEssentialsEventMap[time.eventId] = event.id
              return {
                zoom_event_id: time.eventId,
                zoom_event_type: zoomEventTypeMap[event.eventType],
              }
            })
          }
        })

        const registrationData =
          (await getEssentialsEventZoomInfo({
            events: zoomEvents,
            lyraId: lyraId as string,
            axiosInstance,
          })) ?? []

        // If zoom api fails, just show the events even if they may be unavailable
        if (registrationData.length === 0) return eventsList

        // Check if each event has any available event times
        const hasAvailableEvent = registrationData.reduce((hasAvailableEventMap: Record<string, boolean>, curEvent) => {
          const eventId = zoomEventToEssentialsEventMap[curEvent.zoom_event_id]
          const event = eventsMap[eventId]
          hasAvailableEventMap[eventId] =
            hasAvailableEventMap[eventId] === true ||
            Boolean(event.seatCount - curEvent.current_number_of_registrants > 0)
          return hasAvailableEventMap
        }, {})

        // Return events with available seats
        return eventsList.filter((event) => hasAvailableEvent[event.id])
      }

      let availableEvents: EssentialsEventData[] = []
      const batchSize = 5
      // Process in batches until we have 3 available events
      for (let i = 0; i < essentialsEventsFullList.length && availableEvents.length < 3; i += batchSize) {
        const batch = essentialsEventsFullList.slice(i, i + batchSize)
        const batchResults = await filterForAvailableEvents(batch)
        availableEvents = [...availableEvents, ...batchResults]
      }

      setAvailableEvents(availableEvents)
    }

    fetchAvailableEvents()
  }, [axiosInstance, essentialsEventsFullList, isLoggedIn, lyraId])

  useEffect(() => {
    if (availableEvents && availableEvents?.length > 0) {
      const finalEvents = availableEvents.map((event) => {
        const isRegistered = registeredEvents
          ? registeredEvents.some((registeredEvent) => registeredEvent.id === event.id)
          : false
        return { ...event, isRegistered }
      })
      setUpcomingEvents(finalEvents)
    }
  }, [availableEvents, registeredEvents])

  const eventData = useMemo(() => {
    return upcomingEvents.map((event, index) => {
      return {
        mixpanelData: getAnalyticsPropsForEvent(event, index),
        otherData: {
          slug: event.slug,
        },
      }
    })
  }, [upcomingEvents])

  return useMemo(() => {
    return {
      eventsList: upcomingEvents.slice(0, NUM_UPCOMING_EVENTS),
      eventsMixpanelDataList: eventData.slice(0, NUM_UPCOMING_EVENTS),
    }
  }, [eventData, upcomingEvents])
}
